import { useState } from 'react';
import { Input, Toggle } from '@ab-inbev-maz/compass';
import { useCanvasEditor } from './CanvasEditorContext';
import ColorPickerInput from '../commos/ColorPickerInput';
import { CANVAS_SIZES, type CanvasSizePreset } from './CanvasSizes';

const CanvasWorkspaceTab = () => {
  const {
    gridEnabled,
    setGridEnabled,
    safeZoneEnabled,
    setSafeZoneEnabled,
    safeZoneMarginPercent,
    setSafeZoneMarginPercent,
    snapToGridEnabled,
    setSnapToGridEnabled,
    backgroundColor,
    setBackgroundColor,
    canvasWidth,
    canvasHeight,
    canvasSizeId,
    setCanvasSize,
  } = useCanvasEditor();
  const [customWidth, setCustomWidth] = useState(String(canvasWidth));
  const [customHeight, setCustomHeight] = useState(String(canvasHeight));
  const [customSafeZoneMargin, setCustomSafeZoneMargin] = useState(String(safeZoneMarginPercent));

  const handleSafeZoneMarginChange = (value: string) => {
    setCustomSafeZoneMargin(value);
    const percent = Number.parseInt(value, 10);
    if (percent > 0) setSafeZoneMarginPercent(percent);
  };

  const handleSelectSize = (size: CanvasSizePreset) => {
    setCanvasSize(size.width, size.height, size.id);
    setCustomWidth(String(size.width));
    setCustomHeight(String(size.height));
  };

  const handleCustomWidthChange = (value: string) => {
    setCustomWidth(value);
    const width = Number.parseInt(value, 10);
    if (width > 0) setCanvasSize(width, canvasHeight, 'custom');
  };

  const handleCustomHeightChange = (value: string) => {
    setCustomHeight(value);
    const height = Number.parseInt(value, 10);
    if (height > 0) setCanvasSize(canvasWidth, height, 'custom');
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="text-sm font-bold text-gray-900 mb-2">Color de fondo</h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="h-8 px-3 text-sm font-normal text-gray-600 border border-gray-200 rounded-md cursor-pointer shrink-0"
          >
            Hex
          </button>
          <Input
            id="canvas-bg-hex"
            name="canvas-bg-hex"
            value={backgroundColor}
            onChange={(e) => setBackgroundColor(e.target.value)}
            className="h-8 text-sm font-normal text-gray-600 [--form-field-padding-y:0.35rem] [--form-field-padding-x:0.6rem] [--form-field-default-border:1px_solid_#e5e7eb] [--form-field-default-bg:white]"
          />
          <ColorPickerInput value={backgroundColor} onChange={setBackgroundColor} />
          <button
            type="button"
            onClick={() => setBackgroundColor('transparent')}
            title="Fondo transparente"
            className="w-16 h-8 shrink-0 rounded-md border border-gray-200 bg-[repeating-conic-gradient(#e5e7eb_0%_25%,white_0%_50%)] bg-size-[8px_8px] cursor-pointer"
          />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-bold text-gray-900 mb-2">Tamaño del canva</h3>
        <div className="flex flex-col gap-2">
          {CANVAS_SIZES.map((size) => (
            <button
              key={size.id}
              type="button"
              onClick={() => handleSelectSize(size)}
              className={`flex items-center gap-3 p-3 border rounded-lg text-left cursor-pointer ${
                canvasSizeId === size.id ? 'border-[#FFCC00] bg-[#FFF9E5]' : 'border-gray-200 bg-white'
              }`}
            >
              <span className="flex items-center justify-center w-10 h-10">
                <span
                  className={`${size.shapeClassName} border-2 bg-white rounded-sm ${
                    canvasSizeId === size.id ? 'border-[#FFCC00]' : 'border-gray-400'
                  }`}
                />
              </span>

              <span>
                <span className="block text-sm font-bold text-gray-900">{size.label}</span>
                <span className="block text-xs text-gray-500">{size.dimensions}</span>
              </span>
            </button>
          ))}
        </div>

        <div className="mt-3">
          <h4 className="text-sm font-bold text-gray-900">Personalizado</h4>
          <p className="text-xs text-gray-500 mb-2">Ancho x alto (px)</p>
          <div className="flex items-center gap-2 px-10">
            <Input
              id="canvas-custom-width"
              name="canvas-custom-width"
              value={customWidth}
              onChange={(e) => handleCustomWidthChange(e.target.value)}
              className="flex-1 h-8 text-sm font-normal text-gray-600 [--form-field-padding-y:0.35rem] [--form-field-padding-x:0.6rem] [--form-field-default-border:1px_solid_#e5e7eb] [--form-field-default-bg:white]"
            />
            <span className="text-sm text-gray-500">x</span>
            <Input
              id="canvas-custom-height"
              name="canvas-custom-height"
              value={customHeight}
              onChange={(e) => handleCustomHeightChange(e.target.value)}
              className="flex-1 h-8 text-sm font-normal text-gray-600 [--form-field-padding-y:0.35rem] [--form-field-padding-x:0.6rem] [--form-field-default-border:1px_solid_#e5e7eb] [--form-field-default-bg:white]"
            />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-bold text-gray-900 mb-2">Guías</h3>
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm text-gray-700">Zona segura</span>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <Input
                  id="canvas-safe-zone-margin"
                  name="canvas-safe-zone-margin"
                  value={customSafeZoneMargin}
                  onChange={(e) => handleSafeZoneMarginChange(e.target.value)}
                  className="h-8 shrink-0 text-sm font-normal text-gray-600 [--form-field-container-width:2.75rem] [--form-field-padding-y:0.35rem] [--form-field-padding-x:0.4rem] [--form-field-default-border:1px_solid_#e5e7eb] [--form-field-default-bg:white]"
                />
                <span className="text-sm text-gray-500">%</span>
              </div>
              <Toggle
                id="canvas-safe-zone-toggle"
                name="canvas-safe-zone-toggle"
                checked={safeZoneEnabled}
                onChange={(e) => setSafeZoneEnabled(e.target.checked)}
              />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">Cuadrícula</span>
            <Toggle
              id="canvas-grid-toggle"
              name="canvas-grid-toggle"
              checked={gridEnabled}
              onChange={(e) => setGridEnabled(e.target.checked)}
            />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">Ajustar a guías</span>
            <Toggle
              id="canvas-snap-toggle"
              name="canvas-snap-toggle"
              checked={snapToGridEnabled}
              onChange={(e) => setSnapToGridEnabled(e.target.checked)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CanvasWorkspaceTab;
